import { AppStatus } from '@App';
import {
  getHasBeenAskedForBiometrics,
  setHasBeenAskedForBiometrics
} from '@shared/services/biometrics-storage.service';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to handle post-login biometric setup prompt
 *
 * After successful login, prompts user to enable biometric authentication
 * - Shows modal only if device supports biometrics and user hasn't been asked before
 * - Handles setup acceptance (stores tokens) and decline
 * - Tracks processing state
 */
export const useBiometricSetup = (
  appStatus: AppStatus,
  biometricAvailable: boolean,
  enableBiometrics: () => Promise<boolean>
) => {
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [hasBeenAskedForBiometrics, setHasBeenAskedLocal] = useState(false);

  // Load biometric preferences from storage on mount
  useEffect(() => {
    const loadBiometricPrefs = async () => {
      const asked = await getHasBeenAskedForBiometrics();
      setHasBeenAskedLocal(asked);
    };
    loadBiometricPrefs();
  }, []);

  // Check if we should show biometric setup modal after app is loaded
  useEffect(() => {
    const shouldShowPrompt = appStatus === 'ok' && biometricAvailable && !hasBeenAskedForBiometrics;

    if (shouldShowPrompt) setShowBiometricSetup(true);
  }, [appStatus, biometricAvailable, hasBeenAskedForBiometrics]);

  // Handle biometric setup acceptance
  const handleBiometricAccept = useCallback(async () => {
    try {
      await enableBiometrics();
    } catch (error) {
      console.error('[useBiometricSetup] Error during biometric setup:', error);
    } finally {
      await setHasBeenAskedForBiometrics(true);
      setHasBeenAskedLocal(true);
      setShowBiometricSetup(false);
    }
  }, [enableBiometrics]);

  // Handle biometric setup decline
  const handleBiometricDecline = useCallback(async () => {
    await setHasBeenAskedForBiometrics(true);
    setHasBeenAskedLocal(true);
    setShowBiometricSetup(false);
  }, []);

  return { showBiometricSetup, handleBiometricAccept, handleBiometricDecline };
};
